import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Users, Shield, Check, X, User, Calendar, UserPlus, LogIn } from 'lucide-react';
import { useLocation } from 'wouter';
import { isUnauthorizedError } from '@/lib/authUtils';

interface TeamInvitationWithTeam {
  id: string;
  teamId: string;
  email: string;
  role: string;
  invitationToken: string;
  status: string;
  expiresAt: Date | string | null;
  createdAt: Date | string | null;
  team?: { name: string };
}

interface AcceptInvitationProps {
  token: string;
}

export default function AcceptInvitation({ token }: AcceptInvitationProps) {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [invitationStatus, setInvitationStatus] = useState<'loading' | 'valid' | 'invalid' | 'expired' | 'accepted' | 'error'>('loading');

  // Query to get invitation details
  // Note: queryClient already extracts response.data, so invitationData is the invitation directly
  const { data: invitation, isLoading, error } = useQuery<TeamInvitationWithTeam>({
    queryKey: [`/api/invitations/token/${token}`],
    enabled: !!token,
    retry: false,
  });

  // Accept invitation mutation
  const acceptInvitationMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('POST', `/api/invitations/token/${token}/accept`);
    },
    onSuccess: async () => {
      setInvitationStatus('accepted');
      toast({
        title: "Success",
        description: "You have successfully joined the team!",
      });
      // Refresh auth state so isApproved updates before navigating
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      await queryClient.refetchQueries({ queryKey: ['/api/auth/user'] });
      // Redirect to teams page after a brief delay
      setTimeout(() => {
        navigate('/teams');
      }, 2000);
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You need to log in to accept this invitation.",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 1000);
        return;
      }
      
      toast({
        title: "Error",
        description: error.message || "Failed to accept invitation",
        variant: "destructive",
      });
    },
  });

  // Check invitation status
  useEffect(() => {
    if (invitation) {
      if (invitation.status === 'accepted') {
        setInvitationStatus('accepted');
        // Refresh auth state in case user was auto-approved during a previous acceptance
        queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      } else if (invitation.status === 'revoked') {
        setInvitationStatus('invalid');
      } else if (invitation.expiresAt && new Date() > new Date(invitation.expiresAt)) {
        setInvitationStatus('expired');
      } else if (invitation.status === 'pending') {
        setInvitationStatus('valid');
      }
    } else if (error) {
      setInvitationStatus('invalid');
    }
  }, [invitation, error]);

  // Handle automatic acceptance after login
  useEffect(() => {
    const pendingToken = localStorage.getItem('pendingInvitationToken');
    const userEmail = (user as any)?.email;
    if (isAuthenticated && pendingToken === token && invitationStatus === 'valid' && userEmail === invitation?.email) {
      localStorage.removeItem('pendingInvitationToken');
      // Automatically accept the invitation
      setTimeout(() => {
        acceptInvitationMutation.mutate();
      }, 1000); // Small delay to show the UI first
    }
  }, [isAuthenticated, token, invitationStatus, user, invitation?.email, acceptInvitationMutation]);

  const handleAcceptInvitation = () => {
    const userEmail = (user as any)?.email;
    
    if (!isAuthenticated) {
      // Store the invitation token for after login
      localStorage.setItem('pendingInvitationToken', token);
      toast({
        title: "Authentication Required",
        description: "Redirecting to login. After login, you'll automatically be brought back to accept the invitation.",
      });
      // Redirect to login with return URL
      window.location.href = `/api/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    if (userEmail !== invitation?.email) {
      toast({
        title: "Email Mismatch",
        description: `This invitation is for ${invitation?.email}, but you're logged in as ${userEmail}. Please log in with the correct account or contact the team administrator.`,
        variant: "destructive",
      });
      return;
    }

    acceptInvitationMutation.mutate();
  };

  const getRoleBadgeStyles = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
      case 'manager':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
      case 'technician':
        return 'bg-green-500/20 text-green-300 border-green-500/40';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/40';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-3 w-3 mr-1" />;
      case 'manager':
        return <Users className="h-3 w-3 mr-1" />;
      case 'technician':
        return <User className="h-3 w-3 mr-1" />;
      default:
        return <User className="h-3 w-3 mr-1" />;
    }
  };

  // Only wait for invitation data to load, not auth (auth can resolve in background)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--restore-dark-bg)' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-400" />
          <p className="text-gray-400">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (invitationStatus === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--restore-dark-bg)' }}>
        <Card className="w-full max-w-md border-red-500/30 bg-gray-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
              <X className="h-10 w-10 text-red-400" />
            </div>
            <CardTitle className="text-red-400 text-xl">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent className="text-center pt-2">
            <p className="mb-6 text-gray-400">
              This invitation link is invalid or has been revoked.
            </p>
            <Button 
              onClick={() => navigate('/teams')} 
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Go to Teams
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationStatus === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--restore-dark-bg)' }}>
        <Card className="w-full max-w-md border-amber-500/30 bg-gray-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-500/30">
              <X className="h-10 w-10 text-amber-400" />
            </div>
            <CardTitle className="text-amber-400 text-xl">Invitation Expired</CardTitle>
          </CardHeader>
          <CardContent className="text-center pt-2">
            <p className="mb-6 text-gray-400">
              This invitation has expired. Please request a new invitation from the team administrator.
            </p>
            <Button 
              onClick={() => navigate('/teams')} 
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Go to Teams
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationStatus === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--restore-dark-bg)' }}>
        <Card className="w-full max-w-md border-green-500/30 bg-gray-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/30">
              <Check className="h-10 w-10 text-green-400" />
            </div>
            <CardTitle className="text-green-400 text-xl">Welcome to the Team!</CardTitle>
          </CardHeader>
          <CardContent className="text-center pt-2">
            <p className="mb-6 text-gray-400">
              You have successfully joined the team! Redirecting to Teams page...
            </p>
            <Button 
              onClick={async () => {
                await queryClient.refetchQueries({ queryKey: ['/api/auth/user'] });
                navigate('/teams');
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Go to Teams
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--restore-dark-bg)' }}>
      <Card className="w-full max-w-md border-blue-500/30 bg-gray-900/80 backdrop-blur-sm">
        <CardHeader className="text-center pb-4">
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
            <Users className="h-10 w-10 text-blue-400" />
          </div>
          <CardTitle className="text-white text-2xl">Team Invitation</CardTitle>
          <p className="text-gray-400 text-sm mt-2">You've been invited to join a team</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {invitation && (
            <div className="space-y-6">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Invited Email</p>
                    <p className="font-medium text-white">{invitation.email}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Role:</span>
                    <Badge className={`${getRoleBadgeStyles(invitation.role)} capitalize`}>
                      {getRoleIcon(invitation.role)}
                      {invitation.role}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                {invitation.createdAt && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Invited {new Date(invitation.createdAt).toLocaleDateString()}</span>
                  </div>
                )}
                {invitation.expiresAt && (
                  <div className="flex items-center gap-1">
                    <span>Expires {new Date(invitation.expiresAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {authLoading ? (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                  <p className="text-sm text-gray-400">Checking authentication...</p>
                </div>
              ) : !isAuthenticated ? (
                <div className="space-y-3">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                    <p className="text-sm text-blue-200 font-medium mb-1">
                      This invitation is for
                    </p>
                    <p className="text-base font-semibold text-white mb-2">{invitation.email}</p>
                    <p className="text-xs text-blue-400">
                      Create a new account or log in with that email to join the team.
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      localStorage.setItem('pendingInvitationToken', token);
                      window.location.href = `/signup?invitationToken=${token}&email=${encodeURIComponent(invitation.email)}`;
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-11"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Account & Join Team
                  </Button>
                  <Button
                    onClick={() => {
                      localStorage.setItem('pendingInvitationToken', token);
                      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
                    }}
                    variant="outline"
                    className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white h-11"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Log In with Existing Account
                  </Button>
                </div>
              ) : (user as any)?.email !== invitation.email ? (
                <div className="space-y-3">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                    <p className="text-sm text-red-300">
                      This invitation is for <span className="font-medium">{invitation.email}</span>
                    </p>
                    <p className="text-xs text-red-400 mt-1">
                      You are logged in as {(user as any)?.email}
                    </p>
                  </div>
                  <Button 
                    onClick={() => window.location.href = "/api/logout"}
                    variant="outline"
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    Log Out and Switch Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-sm text-green-300">
                      Ready to join as <span className="font-medium">{(user as any)?.email}</span>
                    </p>
                  </div>
                  <Button 
                    onClick={handleAcceptInvitation}
                    disabled={acceptInvitationMutation.isPending}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base"
                  >
                    {acceptInvitationMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Accept Invitation
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => navigate('/teams')}
                    variant="ghost"
                    className="w-full text-gray-400 hover:text-white hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
